import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { z } from 'zod';
import { api, invalidateData, keys } from '../../api.ts';
import { ohcwSchema } from '../../../shared/schemas.ts';
import type { UserFixed } from '../../../shared/types.ts';
import { applyServerErrors, ErrorBox, Field, Help, Loading, Spinner, useToast } from '../../components/ui.tsx';
import { useUserContext } from './UserLayout.tsx';

type Values = z.input<typeof ohcwSchema>;

export function useFixed(userId: string) {
  return useQuery({ queryKey: keys.fixed(userId), queryFn: () => api.get<UserFixed>(`/users/${encodeURIComponent(userId)}/fixed`) });
}

const str = (v: number | string | null | undefined) => (v === null || v === undefined ? '' : String(v));

export function FixedOhcwPage() {
  const { user } = useUserContext();
  const fixed = useFixed(user.id);
  const toast = useToast();
  const { register, handleSubmit, formState, reset, setError } = useForm<Values, unknown, z.output<typeof ohcwSchema>>({
    resolver: zodResolver(ohcwSchema),
  });
  useEffect(() => {
    if (!fixed.data) return;
    const f = fixed.data;
    reset({
      noOfCountries: str(f.noOfCountries),
      noOfCountriesTotal: str(f.noOfCountriesTotal),
      noOfCountriesDate: str(f.noOfCountriesDate),
      hideFromDivisionReport: f.hideFromDivisionReport,
      cwNoOfCountries: str(f.cwNoOfCountries),
      cwNoOfCountriesTotal: str(f.cwNoOfCountriesTotal),
      cwNoOfCountriesDate: str(f.cwNoOfCountriesDate),
      hideFromCwReport: f.hideFromCwReport,
    });
  }, [fixed.data, reset]);

  const save = useMutation({
    mutationFn: (d: z.output<typeof ohcwSchema>) => api.put(`/users/${encodeURIComponent(user.id)}/fixed/ohcw`, d),
    onSuccess: () => {
      invalidateData();
      toast.ok('Tiedot tallennettu');
    },
    onError: (err) => !applyServerErrors(err, setError) && toast.error(err),
  });

  if (fixed.isLoading) return <Loading />;
  if (fixed.error) return <ErrorBox error={fixed.error} />;
  const e = formState.errors;

  return (
    <form className="form page-narrow" onSubmit={handleSubmit((d) => save.mutate(d))} noValidate>
      <div className="row">
        <p className="muted" style={{ margin: 0, flex: 1 }}>
          Jos syötät arvot, niitä käytetään OH-Divisions- ja OH-CW-taulukoissa QSO:ista laskettujen arvojen sijaan. Päivämäärää
          käytetään tasapisteiden järjestämiseen.
        </p>
        <Help name="KayttajanMuokkausOHCW" />
      </div>
      <fieldset>
        <legend>OH-Divisions</legend>
        <div className="form-grid">
          <Field label="Nykymaat" error={e.noOfCountries} htmlFor="noc">
            <input id="noc" className="input mono" inputMode="numeric" {...register('noOfCountries')} />
          </Field>
          <Field label="Nykymaat + poistetut" error={e.noOfCountriesTotal} htmlFor="noct">
            <input id="noct" className="input mono" inputMode="numeric" {...register('noOfCountriesTotal')} />
          </Field>
          <Field label="Päivämäärä" error={e.noOfCountriesDate} htmlFor="nocd">
            <input id="nocd" className="input" type="date" {...register('noOfCountriesDate')} />
          </Field>
        </div>
        <label className="check" style={{ marginTop: 12 }}>
          <input type="checkbox" {...register('hideFromDivisionReport')} /> Piilota minut Divisions-taulukosta
        </label>
      </fieldset>
      <fieldset>
        <legend>OH-CW</legend>
        <div className="form-grid">
          <Field label="CW-nykymaat" error={e.cwNoOfCountries} htmlFor="cw">
            <input id="cw" className="input mono" inputMode="numeric" {...register('cwNoOfCountries')} />
          </Field>
          <Field label="CW-nykymaat + poistetut" error={e.cwNoOfCountriesTotal} htmlFor="cwt">
            <input id="cwt" className="input mono" inputMode="numeric" {...register('cwNoOfCountriesTotal')} />
          </Field>
          <Field label="Päivämäärä" error={e.cwNoOfCountriesDate} htmlFor="cwd">
            <input id="cwd" className="input" type="date" {...register('cwNoOfCountriesDate')} />
          </Field>
        </div>
        <label className="check" style={{ marginTop: 12 }}>
          <input type="checkbox" {...register('hideFromCwReport')} /> Piilota minut OH-CW-taulukosta
        </label>
      </fieldset>
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>
          {save.isPending ? <Spinner /> : 'Tallenna'}
        </button>
      </div>
    </form>
  );
}
